import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Icon from '../Icon';

interface DrawerProps {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  headerContent?: ReactNode;
  footerContent?: ReactNode | null;
  hideHeader?: boolean;
  closeButtonAriaLabel: string;
  disableCloseOnOutsideClick?: boolean;
  drawerPosition?: 'right' | 'left' | 'bottom';
  className?: string;
  disableCloseButton?: boolean;
  closeButtonClassName?: string;
  contentClassName?: string;
}

interface DrawerHeaderProps {
  onClose?: () => void;
  headerContent?: ReactNode;
  closeButtonAriaLabel: string;
  disableCloseButton?: boolean;
  closeButtonClassName?: string;
}
interface DrawerContentProps {
  children: ReactNode;
  removePaddingTop?: boolean;
  contentClassName?: string;
}

interface DrawerFooterProps {
  footerContent?: ReactNode;
}

const Drawer = ({
  drawerPosition = 'right',
  className,
  isOpen,
  onClose,
  children,
  headerContent,
  footerContent,
  hideHeader,
  closeButtonAriaLabel,
  disableCloseOnOutsideClick = false,
  disableCloseButton = false,
  closeButtonClassName,
  contentClassName,
}: DrawerProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  // SHOULD BE THE SAME VALUE AS DURATION CLASS ON THE DIALOG. VARIABLE IS NOT USED IN THE CLASS SINCE WE THEN NEED TO SAFELIST IT IN TAILWIND CONFIG
  const ANIMATION_DURATION_TIME = 200;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    const focusableElements = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements[0] as HTMLElement;
    const lastFocusableElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    if (isOpen) {
      dialog.showModal();
      document.body.classList.add('overflow-hidden');
    } else {
      setTimeout(() => {
        dialog.close();
        document.body.classList.remove('overflow-hidden');
      }, ANIMATION_DURATION_TIME);
    }

    const lockFocusToModal = (event: KeyboardEvent) => {
      if (firstFocusableElement === null || lastFocusableElement === null)
        return;
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            event.preventDefault();
          }
        }
      }
    };
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!disableCloseOnOutsideClick) {
          onClose?.();
        }
      }
    };
    const handleOutsideClick = (event: MouseEvent) => {
      if (disableCloseOnOutsideClick) return;
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };
    setDialogIsOpen(isOpen);

    dialog.addEventListener('click', handleOutsideClick);
    dialog.addEventListener('keydown', lockFocusToModal);
    dialog.addEventListener('keydown', handleEscKeyPress);

    return () => {
      dialog.removeEventListener('click', handleOutsideClick);
      dialog.removeEventListener('keydown', lockFocusToModal);
      dialog.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [disableCloseOnOutsideClick, isOpen, onClose]);

  const drawerFromRight = drawerPosition === 'right';
  const drawerFromLeft = drawerPosition === 'left';
  const drawerFromBottom = drawerPosition === 'bottom';

  return (
    <>
      <dialog
        className={clsx(
          'fixed inset-[unset] my-0 h-screen transition-all duration-200 ease-out backdrop:bg-black backdrop:bg-opacity-50 backdrop:bg-none',
          drawerFromRight &&
            'bottom-2 right-2 top-2 max-h-[calc(100vh-1rem)] w-[calc(100vw-1rem)] max-w-[512px] translate-x-0  rounded store-screen:max-h-[1080px]',
          drawerFromLeft &&
            'bottom-2 left-2 top-2 max-h-[calc(100vh-1rem)] w-[calc(100vw-1rem)] max-w-[512px] translate-x-0  rounded store-screen:max-h-[1080px]',
          drawerFromBottom &&
            'bottom-0 left-0 right-0 mx-0 h-full max-h-[80%] w-full translate-y-0 overflow-y-auto',
          drawerFromRight &&
            (dialogIsOpen ? 'translate-x-0' : '!translate-x-full'),
          drawerFromLeft &&
            (dialogIsOpen ? 'translate-x-0' : '!-translate-x-full'),
          drawerFromBottom &&
            (dialogIsOpen ? 'translate-y-0' : '!translate-y-full'),
          className
        )}
        ref={dialogRef}
      >
        <div className="h-full w-full" ref={contentRef}>
          <div className="flex h-full flex-col" ref={contentRef}>
            {!hideHeader && (
              <DrawerHeader
                headerContent={headerContent}
                onClose={onClose}
                closeButtonAriaLabel={closeButtonAriaLabel}
                disableCloseButton={disableCloseButton}
                closeButtonClassName={closeButtonClassName}
              />
            )}
            <DrawerContent
              contentClassName={contentClassName}
              removePaddingTop={!hideHeader}
            >
              {children}
            </DrawerContent>
            {footerContent && (
              <DrawerFooter footerContent={footerContent} />
            )}
          </div>
        </div>
      </dialog>
    </>
  );
};

const DrawerContent: FC<DrawerContentProps> = ({
  children,
  removePaddingTop,
  contentClassName,
}) => {
  return (
    <div
      className={clsx(
        contentClassName,
        'flex-1 overflow-auto',
        removePaddingTop && 'pt-0'
      )}
    >
      {children}
    </div>
  );
};
DrawerContent.displayName = 'DrawerContent';

const DrawerFooter: FC<DrawerFooterProps> = ({ footerContent }) => {
  return (
    <footer className="flex justify-center bg-gray-50 p-2">
      {footerContent}
    </footer>
  );
};
DrawerFooter.displayName = 'DrawerFooter';

const DrawerHeader: FC<DrawerHeaderProps> = ({
  headerContent,
  onClose,
  closeButtonAriaLabel,
  disableCloseButton,
  closeButtonClassName,
}) => {
  return (
    <header
      className={clsx(
        'relative flex items-center p-4 capitalize',
        headerContent
          ? 'items-center justify-between gap-4'
          : 'justify-end'
      )}
    >
      {headerContent}
      {!disableCloseButton && (
        <button
          aria-label={closeButtonAriaLabel}
          className={`${closeButtonClassName} -mr-2 -mt-2 p-2 text-md lg:mr-0 lg:mt-0 lg:p-3`}
          onClick={onClose}
        >
          <Icon className="h-5 w-5 md:h-6 md:w-6" name="close" />
        </button>
      )}
    </header>
  );
};
DrawerHeader.displayName = 'DrawerHeader';

export default Drawer;
